/**
 * DuplicateAlertsPanel — "Dedup-Inbox" für die World.
 *
 * Zeigt die offenen Alerts der Collection `World/{uid}/duplicateAlerts`.
 * Pro Alert:
 *   - erklärender Titel ("E-Mail X liegt an Kontakt A UND B")
 *   - Metadaten: Zeitpunkt, Quelle (DM-Finder, Research, …)
 *   - beide Kontakt-Kacheln nebeneinander (Business-Name + IDs)
 *   - Actions: "Zusammenführen" (öffnet Compare-Modal), "Kein Duplikat",
 *     "Später" (schließt die Karte lokal, Alert bleibt offen).
 *
 * V1 (jetzt): Awareness, Dismiss und Deep-Link ins DB-Table.
 * V2 (folgt): Compare-Modal + Merge-Callable — dann öffnet der "Zusammenführen"-
 *   Button ein Side-by-Side Preview + einen Server-seitigen Merge, der die
 *   loser-Aktivitäten (Sends, Notes) auf den Winner überträgt.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import useDuplicateAlerts from './useDuplicateAlerts';
import { modalCardAnim } from './modalAnimations';
import './DuplicateAlertsPanel.css';

const fmtDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} day${diffD === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString('en-US');
};

const sourceLabel = (src) => ({
  dm_finder: 'DM Finder',
  web_research: 'Web Research',
  api_enrichment: 'API Enrichment',
  inline_edit: 'Manual Edit',
  contact_write_trigger: 'Auto Detection',
}[src] || src || 'System');

// Scoring-Regel für den Auto-Mode: Der Kontakt mit den meisten Signalen
// gewinnt. Reihenfolge der Gewichtung ist bewusst so, dass "bereits im
// Outreach / reply erhalten" mehr wiegt als reine Feldfüllung — sonst
// würde eine reine Anreicherung mit Adressdaten einen laufenden Case
// überstimmen. Ties werden über das ältere `timestamp`-Feld gebrochen,
// damit die Auswahl deterministisch ist.
const SCORE_FIELDS = [
  'business_name', 'lead_email', 'lead_website', 'lead_telephone',
  'lead_industry', 'lead_country', 'lead_city', 'lead_address',
  'lead_description', 'lead_socials',
];

function hasVal(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '' && v.trim() !== '-';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}
function countList(v) {
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'string' && v.trim() && v.trim() !== '[]') {
    try { const p = JSON.parse(v); return Array.isArray(p) ? p.length : 0; } catch { return 0; }
  }
  return 0;
}
function scoreContact(d) {
  if (!d) return -1;
  let score = SCORE_FIELDS.reduce((n, k) => n + (hasVal(d[k]) ? 1 : 0), 0);
  if (d.lead_contacted_timestamp || (d.lead_contacted && d.lead_contacted !== 'Not Contacted')) score += 5;
  if (d.lead_reply === true) score += 4;
  if (d.has_prepared_email === true) score += 2;
  score += Math.min(countList(d.lead_notes), 5);
  score += Math.min(countList(d.lead_decision_makers), 3);
  return score;
}
function pickWinner(contacts) {
  const list = Object.values(contacts).filter((c) => c && !c.merged_into);
  if (list.length === 0) return null;
  const scored = list.map((c) => ({
    id: c.id,
    score: scoreContact(c),
    // ältere Docs bevorzugen bei Score-Tie (weniger Churn: der ursprüngliche
    // Kontakt bleibt bestehen, angereicherte Duplikate wandern rein).
    createdMs: toEpochMs(c.timestamp || c.created_at) || Number.MAX_SAFE_INTEGER,
  }));
  scored.sort((a, b) => b.score - a.score || a.createdMs - b.createdMs);
  return scored[0].id;
}
function toEpochMs(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : 0; }
  if (typeof v === 'object' && v.seconds) return v.seconds * 1000;
  return 0;
}

// V3 (2026-08): Das Panel dupliziert kein Merge/Dismiss-UI mehr. Ein Klick
// auf eine Karte leitet zum bereits vorhandenen DuplicateActionModal (dort
// hat der User „Öffnen", „Entfernen", „Zusammenführen" mit voller Contact-
// Card-Info). So bleibt der Bearbeitungs-Flow überall einheitlich — egal
// ob man den Alert übers Panel oder das rote Warn-Icon in der Row erreicht.
export default function DuplicateAlertsPanel({ userId, onClose, onOpenAlert }) {
  const { openAlerts, loading } = useDuplicateAlerts(userId);

  const visibleAlerts = useMemo(() => openAlerts, [openAlerts]);

  // Auto-Mode State. Zustandsübersicht:
  //   idle       → normale Alerts-Liste, „Auto-resolve" verfügbar
  //   preview    → Winner-Vorauswahl je Alert wird angezeigt, bevor gemergt wird
  //   running    → sequentieller Merge-Loop läuft, Progress + Live-Ergebnisse
  //   done       → Loop fertig, Summary sichtbar; User kann schließen
  const [autoState, setAutoState] = useState('idle');
  const [autoPreview, setAutoPreview] = useState([]); // [{ alertId, winner, losers, email }]
  const [autoProgress, setAutoProgress] = useState({ done: 0, total: 0, currentAlert: null });
  const [autoResults, setAutoResults] = useState({ resolved: 0, skipped: 0, failed: [] });
  const [autoError, setAutoError] = useState(null);

  const handleOpen = (alertItem) => {
    if (typeof onOpenAlert === 'function') {
      onOpenAlert(alertItem);
      onClose && onClose();
    }
  };

  // Preview vorbereiten: für jeden Alert holen wir die involvierten Kontakte,
  // ermitteln den Winner via scoreContact() und bauen ein Plan-Array. Dieser
  // Read-Only-Schritt ist bewusst separat vom eigentlichen Merge — der User
  // soll Winner + Verlierer erst sehen, bevor er auf „Confirm" klickt.
  const prepareAutoPreview = useCallback(async () => {
    if (visibleAlerts.length === 0) return;
    setAutoError(null);
    setAutoState('preview-loading');
    try {
      const db = getFirestore();
      const plan = [];
      for (const alertItem of visibleAlerts) {
        const ids = uniqueContactIds(alertItem);
        if (ids.length < 2) {
          plan.push({ alertId: alertItem.id, skipped: true, reason: 'stale', email: alertItem.emailInQuestion });
          continue;
        }
        const snaps = await Promise.all(
          ids.map((id) => getDoc(doc(db, `World/${userId}/db/${id}`))),
        );
        const contactMap = {};
        snaps.forEach((s, i) => {
          if (s.exists()) contactMap[ids[i]] = { id: s.id, ...s.data() };
        });
        const active = Object.values(contactMap).filter((c) => c && !c.merged_into);
        if (active.length < 2) {
          plan.push({ alertId: alertItem.id, skipped: true, reason: 'already-resolved', email: alertItem.emailInQuestion });
          continue;
        }
        const winnerId = pickWinner(contactMap);
        const winner = contactMap[winnerId];
        const losers = active.filter((c) => c.id !== winnerId);
        plan.push({
          alertId: alertItem.id,
          email: alertItem.emailInQuestion,
          winner: {
            id: winner.id,
            name: winner.business_name || '(no name)',
            score: scoreContact(winner),
            contacted: !!(winner.lead_contacted_timestamp
              || (winner.lead_contacted && winner.lead_contacted !== 'Not Contacted')),
          },
          losers: losers.map((l) => ({
            id: l.id,
            name: l.business_name || '(no name)',
            score: scoreContact(l),
          })),
        });
      }
      setAutoPreview(plan);
      setAutoState('preview');
    } catch (err) {
      console.error('[DuplicateAlertsPanel] preview failed:', err);
      setAutoError(err && err.message ? err.message : 'Preview failed');
      setAutoState('idle');
    }
  }, [visibleAlerts, userId]);

  // Merge-Loop: iteriert pro Alert und pro Loser sequentiell durch. Fehler
  // eines einzelnen Losers stoppen nicht die Batch — sie werden gesammelt
  // und am Ende zusammen präsentiert (analog zum DuplicateMergeModal).
  const runAutoMerge = useCallback(async () => {
    setAutoState('running');
    const call = httpsCallable(functions, 'mergeContactPair');
    const totalMerges = autoPreview.reduce((sum, p) => sum + (p.skipped ? 0 : p.losers.length), 0);
    setAutoProgress({ done: 0, total: totalMerges, currentAlert: null });
    let done = 0;
    let resolved = 0;
    let skipped = 0;
    const failed = [];
    for (const step of autoPreview) {
      if (step.skipped) {
        skipped += 1;
        continue;
      }
      setAutoProgress({ done, total: totalMerges, currentAlert: step.email || step.alertId });
      let stepHadFailure = false;
      for (const loser of step.losers) {
        try {
          const res = await call({
            userId,
            winnerId: step.winner.id,
            loserId: loser.id,
            alertId: step.alertId,
          });
          if (!res.data || !res.data.ok) throw new Error('merge failed');
        } catch (err) {
          stepHadFailure = true;
          failed.push({
            alertId: step.alertId, email: step.email,
            loserId: loser.id, loserName: loser.name,
            error: err && err.message ? err.message : String(err),
          });
        }
        done += 1;
        setAutoProgress({ done, total: totalMerges, currentAlert: step.email || step.alertId });
      }
      if (!stepHadFailure) resolved += 1;
    }
    setAutoResults({ resolved, skipped, failed });
    setAutoState('done');
  }, [autoPreview, userId]);

  const cancelAuto = () => {
    if (autoState === 'running') return; // während Loop lassen wir kein Abbrechen zu
    setAutoState('idle');
    setAutoPreview([]);
    setAutoResults({ resolved: 0, skipped: 0, failed: [] });
    setAutoProgress({ done: 0, total: 0, currentAlert: null });
  };

  const inAutoMode = autoState !== 'idle';

  // Wichtig: das Panel MUSS als Portal in `document.body` gerendert werden.
  // Andernfalls landet es innerhalb der sticky `.world-db-header-container`
  // (die einen eigenen Stacking-Context erzeugt) und wird vom Header überdeckt,
  // obwohl das Overlay ein hohes `zIndex` hat.
  const panelNode = (
    <div
      className="dup-panel-overlay"
      onClick={autoState === 'running' ? undefined : onClose}
    >
      <div className="dup-overlay-slot">
        <div
          className="dup-panel dup-panel-sheet"
          style={{ animation: modalCardAnim }}
          onClick={(e) => e.stopPropagation()}
        >
        <div className="dup-panel-header">
          <div className="dup-panel-headerCopy">
            <h2 className="dup-panel-title">Duplicate Warnings</h2>
            <p className="dup-panel-subtitle">
              {loading
                ? 'Loading…'
                : visibleAlerts.length === 0
                  ? 'No open warnings — your contact data is clean.'
                  : `${visibleAlerts.length} contact${visibleAlerts.length === 1 ? '' : 's'} share an email with other entries.`}
            </p>
          </div>
          <div className="dup-panel-headerActions">
            {!inAutoMode && !loading && visibleAlerts.length >= 1 && (
              <button
                type="button"
                onClick={prepareAutoPreview}
                className="dup-panel-autoBtn"
                title="Automatically merge every warning: the contact with the most info wins"
              >
                Auto-resolve {visibleAlerts.length}
              </button>
            )}
            {autoState !== 'running' && (
              <button type="button" onClick={onClose} className="dup-panel-close" title="Close" aria-label="Close">×</button>
            )}
          </div>
        </div>

        <div className="dup-panel-body">
          {!loading && !inAutoMode && visibleAlerts.length === 0 && (
            <EmptyState />
          )}

          {!inAutoMode && autoError && (
            <div className="dup-panel-error">{autoError}</div>
          )}

          {!inAutoMode && visibleAlerts.map((a) => (
            <AlertCard
              key={a.id}
              alert={a}
              onOpen={() => handleOpen(a)}
            />
          ))}

          {autoState === 'preview-loading' && (
            <div className="dup-panel-center">Analyzing contacts…</div>
          )}

          {(autoState === 'preview' || autoState === 'running' || autoState === 'done') && (
            <AutoModePanel
              state={autoState}
              plan={autoPreview}
              progress={autoProgress}
              results={autoResults}
              onConfirm={runAutoMerge}
              onCancel={cancelAuto}
              onClose={onClose}
            />
          )}
        </div>

        <div className="dup-panel-footer">
          <span className="dup-panel-footerHint">
            {autoState === 'idle'
              ? 'New warnings appear automatically whenever research or automation adds an email to a second contact.'
              : autoState === 'preview'
                ? 'Preview only — nothing has been merged yet. Review the plan and confirm to proceed.'
                : autoState === 'running'
                  ? 'Merging in progress — please keep this window open.'
                  : 'All done. Merged contacts are kept as history; only the winner remains in the table.'}
          </span>
        </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return panelNode;
  return createPortal(panelNode, document.body);
}

function uniqueContactIds(alertItem) {
  const list = [alertItem.currentContactId, ...(Array.isArray(alertItem.otherContactIds) ? alertItem.otherContactIds : [])];
  return Array.from(new Set(list.filter(Boolean)));
}

function AutoModePanel({ state, plan, progress, results, onConfirm, onCancel, onClose }) {
  const actionable = plan.filter((p) => !p.skipped);
  const skipped = plan.filter((p) => p.skipped);
  const totalMerges = actionable.reduce((s, p) => s + p.losers.length, 0);

  return (
    <div className="dup-auto">
      <div className="dup-auto-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="dup-auto-headTitle">
            {state === 'preview' && 'Auto-resolve plan'}
            {state === 'running' && 'Auto-resolving duplicates…'}
            {state === 'done' && 'Auto-resolve finished'}
          </div>
          <div className="dup-auto-headSub">
            {state === 'preview' && (
              <>
                <strong>{actionable.length}</strong> warning{actionable.length === 1 ? '' : 's'} will be resolved
                {' '}({totalMerges} merge{totalMerges === 1 ? '' : 's'} total)
                {skipped.length > 0 && `, ${skipped.length} skipped`}.
              </>
            )}
            {state === 'running' && (
              <>
                {progress.done}/{progress.total} merged
                {progress.currentAlert && (
                  <> · current: <code className="dup-auto-code">{progress.currentAlert}</code></>
                )}
              </>
            )}
            {state === 'done' && (
              <>
                <strong>{results.resolved}</strong> resolved
                {results.skipped > 0 && `, ${results.skipped} skipped`}
                {results.failed.length > 0 && `, ${results.failed.length} failed`}.
              </>
            )}
          </div>
        </div>
      </div>

      {state === 'running' && (
        <div className="dup-auto-progress">
          <div
            className="dup-auto-progressFill"
            style={{ width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%` }}
          />
        </div>
      )}

      {state === 'preview' && (
        <div className="dup-auto-rules">
          <div className="dup-auto-rulesTitle">Winner selection rule</div>
          <div className="dup-auto-rulesText">
            Contact with the most filled fields wins — bonus weight for
            already-contacted, replied, prepared emails, notes and decision
            makers. Ties go to the older record. All other contacts are merged
            into the winner (no data loss).
          </div>
        </div>
      )}

      <div className="dup-auto-list">
        {actionable.map((step) => (
          <AutoStepCard key={step.alertId} step={step} state={state} />
        ))}
        {skipped.length > 0 && (
          <div className="dup-auto-skipped">
            <i className="fa fa-forward" aria-hidden />
            {' '}{skipped.length} warning{skipped.length === 1 ? '' : 's'} skipped
            (already resolved or only one active contact remains)
          </div>
        )}
      </div>

      {state === 'done' && results.failed.length > 0 && (
        <div className="dup-auto-failures">
          <strong>Failures ({results.failed.length}):</strong>
          <ul>
            {results.failed.slice(0, 5).map((f, i) => (
              <li key={i}>{f.loserName}: {f.error}</li>
            ))}
            {results.failed.length > 5 && (
              <li>… and {results.failed.length - 5} more</li>
            )}
          </ul>
        </div>
      )}

      <div className="dup-auto-actions">
        {state === 'preview' && (
          <>
            <button type="button" onClick={onCancel} className="dup-btn dup-btn--ghost">
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={actionable.length === 0}
              className="dup-btn dup-btn--primary"
            >
              <i className="fa fa-check" aria-hidden />
              Confirm and merge
            </button>
          </>
        )}
        {state === 'done' && (
          <button type="button" onClick={onClose} className="dup-btn dup-btn--primary">
            Close
          </button>
        )}
      </div>
    </div>
  );
}

function AutoStepCard({ step, state }) {
  return (
    <div className="dup-step">
      <div className="dup-step-email">
        <i className="fa fa-envelope" aria-hidden />
        <code>{step.email || '(no email)'}</code>
      </div>
      <div className="dup-step-row">
        <div className="dup-step-winner">
          <div className="dup-step-kicker">
            <i className="fa fa-crown" aria-hidden />
            Winner
            <span className="dup-step-score">score {step.winner.score}</span>
          </div>
          <div className="dup-step-name">{step.winner.name}</div>
          {step.winner.contacted && (
            <div className="dup-step-contacted">
              <i className="fa fa-paper-plane" aria-hidden />
              already contacted
            </div>
          )}
        </div>
        <div className="dup-step-arrow" aria-hidden>
          <i className="fa fa-arrow-left" />
        </div>
        <div>
          {step.losers.map((l) => (
            <div key={l.id} className="dup-step-loser">
              <div className="dup-step-kicker">
                Merge into winner
                <span className="dup-step-score dup-step-score--lite">score {l.score}</span>
              </div>
              <div className="dup-step-name">{l.name}</div>
            </div>
          ))}
        </div>
      </div>
      {state === 'done' && (
        <div className="dup-step-done">
          <i className="fa fa-check-circle" aria-hidden />
          Merged
        </div>
      )}
    </div>
  );
}

function AlertCard({ alert, onOpen }) {
  const others = Array.isArray(alert.otherContactIds) ? alert.otherContactIds : [];
  const otherNames = Array.isArray(alert.otherBusinessNames) ? alert.otherBusinessNames : [];
  const email = alert.emailInQuestion || '—';
  const totalContacts = others.length + 1;

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen && onOpen(); }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKey}
      className="dup-alert"
      title="Open — review and resolve contacts"
    >
      <div className="dup-alert-top">
        <div className="dup-alert-meta">
          <span className="dup-alert-source">{sourceLabel(alert.source)}</span>
          <span className="dup-alert-time">{fmtDate(alert.createdAt)}</span>
        </div>
        <div className="dup-alert-review" aria-hidden>
          Review <i className="fa fa-arrow-right" />
        </div>
      </div>

      <p className="dup-alert-copy">
        The email address{' '}
        <span className="dup-alert-email">{email}</span>{' '}
        is now attached to{' '}
        <strong>{totalContacts} contacts</strong>.
      </p>

      <div className="dup-alert-contacts">
        <ContactTile
          heading="Recently edited"
          name={alert.currentBusinessName || '(no name)'}
          isCurrent
        />
        {others.slice(0, 3).map((id, idx) => (
          <ContactTile
            key={id}
            heading={idx === 0 ? 'Collides with' : 'And'}
            name={otherNames[idx] || '(no name)'}
          />
        ))}
        {others.length > 3 && (
          <div className="dup-tile dup-tile--more">
            +{others.length - 3} more
          </div>
        )}
      </div>
    </div>
  );
}

// Kompakte Read-Only-Tile. Detail-Öffnen und Merge/Remove passieren im
// nachgelagerten DuplicateActionModal — nicht mehr hier im Panel.
function ContactTile({ heading, name, isCurrent }) {
  return (
    <div className={`dup-tile${isCurrent ? ' dup-tile--current' : ''}`}>
      <div className="dup-tile-label">{heading}</div>
      <div className="dup-tile-name">{name}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="dup-empty">
      <div className="dup-empty-icon" aria-hidden>
        <i className="fa fa-check" />
      </div>
      <div className="dup-empty-title">All clean</div>
      <div className="dup-empty-text">
        No open duplicate warnings. New warnings appear here automatically
        whenever the system detects a colliding email.
      </div>
    </div>
  );
}
